/**
 * 인증 힌트 쿠키(`auth_state`) 헬퍼.
 *
 * access_token / refresh_token 은 HttpOnly 라 JS 가 읽을 수 없다. 그래서 BE 가 함께
 * 굽는 비민감 플래그 쿠키(httpOnly=false, 값은 "1")의 **존재 여부**로 "로그인되어 있을
 * 가능성"만 판단하고, 실제 신원은 `/v1/auth/me` 로 검증한다.
 *
 * ── 서브도메인 SSO ───────────────────────────────────────────────────────
 * BE 가 `COOKIE_DOMAIN=.example.com` 으로 굽기 때문에 `www` / `console` 같은 형제
 * 서브도메인이 같은 세션을 공유한다. console-web 이 자체 로그인을 갖지 않는 근거다.
 * 로컬(localhost)은 Domain 속성을 못 쓰므로 포트만 다른 두 앱은 쿠키를 공유하지
 * 못한다 — `*.lvh.me` 처럼 실제 도메인 형태로 띄워야 SSO 검증이 된다.
 */

/**
 * dev 는 prod 하위 도메인이라 prod 쿠키도 받는다 — dev 에서만 `dev_` 를 붙여 prod 쿠키를
 * 읽지도 지우지도 않는다. BE `@lib/shared/auth` withEnvCookiePrefix 와 규칙이 같아야 한다.
 */
export const envCookieName = (name: string): string =>
  import.meta.env.VITE_APP_ENV === 'development' ? `dev_${name}` : name;

/** auth-service COOKIE_NAMES.AUTH_STATE */
export const AUTH_STATE_COOKIE = envCookieName('auth_state');

/**
 * 현재 호스트와 그 상위 도메인들. (`console.example.com` →
 * `console.example.com`, `example.com`)
 * eTLD 에는 쿠키를 설정할 수 없으므로 마지막 2개 라벨까지만 만든다.
 */
function domainCandidates(): string[] {
  const host = window.location.hostname;
  // IP·localhost 는 Domain 속성을 쓰지 않는다.
  if (!host.includes('.') || /^[\d.]+$/.test(host)) return [];

  const labels = host.split('.');
  const out: string[] = [];
  for (let i = 0; i <= labels.length - 2; i++) out.push(labels.slice(i).join('.'));
  return out;
}

export interface AuthStateCookie {
  readonly name: string;
  has(): boolean;
  clear(): void;
}

/**
 * 쿠키 이름을 주입받는 팩토리.
 * admin-service 는 `admin_auth_state` 라는 **다른 이름**을 쓴다 — 같은 도메인에 두
 * 세션이 공존할 수 있으므로 이름을 하드코딩하지 않는다.
 */
export function createAuthStateCookie(name: string): AuthStateCookie {
  const has = (): boolean => {
    if (typeof document === 'undefined') return false;
    return document.cookie
      .split('; ')
      .some((entry) => entry.startsWith(`${name}=`));
  };

  /**
   * 로그아웃/인증 실패 시 프론트가 힌트를 즉시 지워 UI 깜빡임과 재-refresh 루프를 막는다.
   *
   * ⚠️ 삭제는 **설정 당시의 Path 와 Domain 이 모두 일치**해야 먹는다. 서버는
   * `COOKIE_DOMAIN` 이 있으면 `Domain=.example.com` 으로 굽는데, Domain 없이 지우면
   * 호스트 전용 쿠키만 지워지고 도메인 쿠키는 그대로 남는다. 그러면 로그아웃 후에도
   * has() 가 계속 true 라 모든 진입이 401 → refresh → 리다이렉트를 반복한다.
   * `COOKIE_DOMAIN` 값을 프론트가 알 수 없으므로 후보 도메인을 전부 시도한다.
   */
  const clear = (): void => {
    if (typeof document === 'undefined') return;

    const base = `${name}=; Path=/; Max-Age=0; SameSite=Lax`;
    document.cookie = base;
    for (const domain of domainCandidates()) {
      document.cookie = `${base}; Domain=${domain}`;
      document.cookie = `${base}; Domain=.${domain}`;
    }
  };

  return { name, has, clear };
}

/** 플랫폼(auth-service) 세션 힌트. web / console-web 공용. */
export const authStateCookie = createAuthStateCookie(AUTH_STATE_COOKIE);

export const hasAuthStateCookie = (): boolean => authStateCookie.has();
export const clearAuthStateCookie = (): void => authStateCookie.clear();
